/**
 * MobileTabBar — bottom-docked segmented control for the portrait-mode BaseState.
 *
 * Usage: create one with a list of tab labels and an onChange callback. The
 * caller repositions it at screen bottom (respecting safe-area-inset-bottom)
 * and calls `setActive(i)` whenever the active tab changes externally.
 */
import { Container, Graphics, Text } from 'pixi.js';
import {
  ACCENT, ACCENT_DIM, PANEL_FILL, FONT_SUBHEADER, TEXT_PRIMARY, TEXT_MUTED, GUGI_SHADOW,
} from './LayoutConstants';

export interface MobileTabBarOptions {
  /** Full pixel width to render across. Typically `vp.w`. */
  width: number;
  /** Bar height in pixels. Default 64. */
  height?: number;
}

export class MobileTabBar extends Container {
  private tabs: { label: string; gfx: Graphics; text: Text; badge: Graphics }[] = [];
  private activeIdx = 0;
  private barW: number;
  private barH: number;
  private onChange: (idx: number) => void;

  constructor(labels: string[], onChange: (idx: number) => void, opts: MobileTabBarOptions) {
    super();
    this.barW = opts.width;
    this.barH = opts.height ?? 64;
    this.onChange = onChange;

    const bg = new Graphics();
    bg.rect(0, 0, this.barW, this.barH);
    bg.fill(PANEL_FILL);
    bg.moveTo(0, 0);
    bg.lineTo(this.barW, 0);
    bg.stroke({ color: ACCENT_DIM, width: 1 });
    bg.moveTo(0, 2);
    bg.lineTo(this.barW, 2);
    bg.stroke({ color: ACCENT, width: 2, alpha: 0.6 });
    this.addChild(bg);

    const tabW = this.barW / labels.length;
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i]!;
      const tab = new Container();
      tab.position.set(i * tabW, 0);

      const tabGfx = new Graphics();
      tab.addChild(tabGfx);

      const tabText = new Text({
        text: label,
        style: { ...FONT_SUBHEADER, fill: TEXT_MUTED, dropShadow: GUGI_SHADOW },
      });
      tabText.anchor.set(0.5, 0.5);
      tabText.position.set(tabW / 2, this.barH / 2);
      tab.addChild(tabText);

      // Badge — small red dot in the top-right corner, hidden by default
      const badge = new Graphics();
      badge.visible = false;
      tab.addChild(badge);

      tab.eventMode = 'static';
      tab.cursor = 'pointer';
      tab.hitArea = { contains: (x, y) => x >= 0 && x <= tabW && y >= 0 && y <= this.barH };
      tab.on('pointerdown', () => this.setActive(i));

      this.addChild(tab);
      this.tabs.push({ label, gfx: tabGfx, text: tabText, badge });
    }

    this.renderActive();
  }

  /** Toggle the small red corner dot on a tab (e.g. unspent stat points). */
  setBadge(idx: number, show: boolean): void {
    const tab = this.tabs[idx];
    if (!tab) return;
    const tabW = this.barW / this.tabs.length;
    const r = 5;
    const cx = tabW - r - 10;
    const cy = r + 10;
    const g = tab.badge;
    g.clear();
    if (show) {
      g.circle(cx, cy, r);
      g.fill(ACCENT);
      g.circle(cx, cy, r);
      g.stroke({ color: 0x000000, width: 1, alpha: 0.6 });
    }
    g.visible = show;
  }

  setActive(idx: number): void {
    if (idx === this.activeIdx || idx < 0 || idx >= this.tabs.length) return;
    this.activeIdx = idx;
    this.renderActive();
    this.onChange(idx);
  }

  get active(): number {
    return this.activeIdx;
  }

  private renderActive(): void {
    const tabW = this.barW / this.tabs.length;
    for (let i = 0; i < this.tabs.length; i++) {
      const tab = this.tabs[i]!;
      tab.gfx.clear();
      if (i === this.activeIdx) {
        tab.gfx.rect(6, 6, tabW - 12, this.barH - 12);
        tab.gfx.fill({ color: ACCENT, alpha: 0.12 });
        tab.gfx.rect(0, 0, tabW, 3);
        tab.gfx.fill(ACCENT);
        tab.text.style.fill = TEXT_PRIMARY;
      } else {
        tab.text.style.fill = TEXT_MUTED;
      }
    }
  }
}
